import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useState, useCallback, useRef, useId } from 'react';
import type { ReactNode } from 'react';
import useScroll, { SCROLL_OFFSET } from '@/hooks/useScroll.js';
import { useDarkSectionSplitOverlay } from '@/hooks/useElementOverDarkSection';
import styles from './StickyScrollLayout.module.scss';
export interface StickyScrollStep {
  /** Optional anchor id applied to the rendered content block. */
  id?: string;
  label: ReactNode;
  content: ReactNode;
  /**
   * Optional visual for the step: the layout's stage (Phase 6, the
   * admissions application process). A layout with a stage keeps a 4:3
   * frame in the sticky sidebar, under `sidebarHeader` and above the steps
   * nav, where the active step's stage rises and fades in (no motion under
   * `prefers-reduced-motion`); its steps each take a screen's height with
   * their content centred, and the step lit is the one at the viewport's
   * middle. Below `lg`, where the sidebar flows above the steps, each
   * step's stage sits above its own content instead. The node fills the
   * frame: pass a cover-fit image such as Helios `Media`. Decorative: the
   * frames are `aria-hidden` and `inert`, so the step's content carries the
   * meaning and nothing in a stage is interactive (give images `alt=""`).
   */
  stage?: ReactNode;
  /** Visual treatment for this content block and sticky nav contrast. */
  tone?: 'default' | 'dark';
  /** @deprecated Use `tone="dark"` instead. */
  dark?: boolean;
}
export type StickyScrollLayoutVariant = 'default' | 'wide' | 'split';
export type StickyScrollLayoutContainer = 'default' | 'mdWide' | 'wide';
export type StickyScrollLayoutHeaderPlacement = 'above' | 'sidebar';
export type StickyScrollLayoutStepsNav = 'default' | 'none';
export interface StickyScrollLayoutProps {
  steps: StickyScrollStep[];
  /**
   * The sticky sidebar's width from `lg`: `default` at most 360px, `wide`
   * at most 500px, `split` a share of the row (the redesign's admissions
   * process: 1.08 to the steps' 0.92, at most 700px), room for a heading
   * pinned over a stage (`headerPlacement="sidebar"`).
   */
  variant?: StickyScrollLayoutVariant;
  /** Controls the outer page alignment independently from sidebar width. */
  container?: StickyScrollLayoutContainer;
  /**
   * Optional heading row (the section's title and lead), rendered once.
   * With `headerPlacement="above"` (the default) it sits above both
   * columns at the layout's width, not sticky, the section gap above the
   * columns: the sidebar keeps its height for a stage and the steps nav.
   */
  header?: ReactNode;
  /**
   * Where `header` renders: `above` both columns (the default), or
   * `sidebar`, at the top of the sticky sidebar, so the title and lead pin
   * together with the stage (or the steps nav) while the steps scroll past
   * (the redesign's admissions process). Below `lg`, where the sidebar
   * flows above the steps, it reads as the heading row. Rendered once, with
   * no dark-step overlay copy (unlike `sidebarHeader`): use it on layouts
   * without `tone="dark"` steps.
   */
  headerPlacement?: StickyScrollLayoutHeaderPlacement;
  /**
   * The sticky steps nav (a button per step label, the active one ruled):
   * `default` renders it from `lg`; `none` omits the `<nav>` entirely (the
   * redesign's admissions process, whose stage alone follows the steps).
   * The active step is still tracked, so a stage keeps following the step
   * in view; the steps' labels are then unused.
   */
  stepsNav?: StickyScrollLayoutStepsNav;
  /** Optional content rendered inside the sticky sidebar above the steps nav. */
  sidebarHeader?: ReactNode;
  /**
   * Optional content rendered once inside the sticky sidebar after the steps
   * nav, such as the page's apply link. It shows at every width: below `lg`
   * the nav is hidden and the sidebar flows above the steps, so the footer is
   * what the sidebar shows there. Unlike `sidebarHeader` it has no dark-step
   * overlay copy, so a link in it is rendered (and focusable) once; use it on
   * layouts without `tone="dark"` steps.
   */
  sidebarFooter?: ReactNode;
}
function getScrollContainer(el: HTMLElement) {
  if (typeof window === 'undefined') return null;
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const {
      overflowY
    } = window.getComputedStyle(parent);
    const scrolls = ['auto', 'scroll', 'overlay'].includes(overflowY);
    if (parent.hasAttribute('data-helios-scroll-container') || scrolls) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}
function hasStage(step: StickyScrollStep) {
  return step.stage !== undefined && step.stage !== null && step.stage !== false;
}
function StickyScrollLayout(t0) {
  const $ = _c(65);
  const {
    steps,
    variant: t1,
    container: t2,
    header,
    headerPlacement: t3,
    stepsNav: t4,
    sidebarHeader,
    sidebarFooter
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const container = t2 === undefined ? "default" : t2;
  const headerPlacement = t3 === undefined ? "above" : t3;
  const stepsNav = t4 === undefined ? "default" : t4;
  const [activeStep, setActiveStep] = useState(1);
  const [scrollContainerEl, setScrollContainerEl] = useState(null);
  const containerRef = useRef(null);
  const instanceId = useId();
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => setActiveStep(value);
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const onSelection = t5;
  const staged = steps.some(hasStage);
  const pinnedHeader = headerPlacement === "sidebar";
  const showNav = stepsNav !== "none";
  let t6;
  if ($[1] !== steps) {
    t6 = steps.some(_temp);
    $[1] = steps;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const hasDarkStep = t6;
  const lastStep = steps.at(-1);
  const endsDark = Boolean(lastStep && (lastStep.tone === "dark" || lastStep.dark));
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = node => {
      containerRef.current = node;
      setScrollContainerEl(node ? getScrollContainer(node) : null);
    };
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const setContainerRef = t7;
  let t8;
  if ($[4] !== scrollContainerEl) {
    t8 = () => {
      const height = scrollContainerEl ? scrollContainerEl.clientHeight : window.innerHeight;
      return height / 2 - SCROLL_OFFSET;
    };
    $[4] = scrollContainerEl;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const readAtMiddle = t8;
  const t9 = `[data-sticky-scroll-id="${instanceId}"] [data-scroll-step]`;
  const t10 = staged ? readAtMiddle : undefined;
  let t11;
  if ($[6] !== scrollContainerEl || $[7] !== t10 || $[8] !== t9) {
    t11 = {
      selectors: t9,
      dataKey: "scrollStep",
      onSelection,
      getTriggerOffsetForEl: t10,
      containerEl: scrollContainerEl
    };
    $[6] = scrollContainerEl;
    $[7] = t10;
    $[8] = t9;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const {
    scrollTo
  } = useScroll(t11);
  const t12 = `[data-sticky-scroll-id="${instanceId}"] [data-split-overlay-target]`;
  const t13 = `[data-sticky-scroll-id="${instanceId}"] [data-sticky-scroll-dark-section]`;
  const t14 = !hasDarkStep;
  let t15;
  if ($[10] !== scrollContainerEl || $[11] !== t12 || $[12] !== t13 || $[13] !== t14) {
    t15 = {
      targetSelector: t12,
      darkSectionSelector: t13,
      disabled: t14,
      containerEl: scrollContainerEl
    };
    $[10] = scrollContainerEl;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  useDarkSectionSplitOverlay(t15);
  let t16;
  if ($[15] !== scrollTo) {
    t16 = index => {
      const el = containerRef.current?.querySelector(`[data-scroll-step="${index}"]`);
      if (el) {
        scrollTo(el);
      }
    };
    $[15] = scrollTo;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  const handleStepClick = t16;
  const t17 = endsDark ? "padded" : undefined;
  const t18 = "";
  const t19 = staged ? "" : undefined;
  const t20 = classNames(styles["helios-sticky-scroll"], styles[`helios-sticky-scroll--${variant}`], {
    [styles["helios-sticky-scroll--staged"]]: staged,
    [styles["helios-sticky-scroll--nav-none"]]: !showNav
  });
  const t21 = styles[`helios-sticky-scroll__container--${container}`];
  let t22;
  if ($[17] !== t21) {
    t22 = classNames(styles["helios-sticky-scroll__container"], t21);
    $[17] = t21;
    $[18] = t22;
  } else {
    t22 = $[18];
  }
  let t23;
  if ($[19] !== header || $[20] !== pinnedHeader) {
    t23 = header && !pinnedHeader && <div className={styles["helios-sticky-scroll__header"]} data-sticky-scroll-header="">{header}</div>;
    $[19] = header;
    $[20] = pinnedHeader;
    $[21] = t23;
  } else {
    t23 = $[21];
  }
  let t24;
  if ($[22] !== header || $[23] !== pinnedHeader) {
    t24 = header && pinnedHeader && <div className={classNames(styles["helios-sticky-scroll__header"], styles["helios-sticky-scroll__header--sidebar"])} data-sticky-scroll-header="sidebar">{header}</div>;
    $[22] = header;
    $[23] = pinnedHeader;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  if ($[25] !== sidebarHeader) {
    t25 = sidebarHeader && <div className={styles["helios-sticky-scroll__sidebar-header"]} data-split-overlay-target={true}><div className={styles["helios-sticky-scroll__sidebar-header-content"]}>{sidebarHeader}</div><div className={classNames(styles["helios-sticky-scroll__sidebar-header-content"], styles["helios-sticky-scroll__sidebar-header-content--dark-overlay"])} aria-hidden="true">{sidebarHeader}</div></div>;
    $[25] = sidebarHeader;
    $[26] = t25;
  } else {
    t25 = $[26];
  }
  let t26;
  if ($[27] !== activeStep || $[28] !== staged || $[29] !== steps) {
    t26 = staged && <div className={styles["helios-sticky-scroll__stage"]} data-sticky-scroll-stage="" aria-hidden="true" inert={true}><div className={styles["helios-sticky-scroll__stage-frame"]}>{steps.map((step_0, i) => hasStage(step_0) ? <div key={i} className={styles["helios-sticky-scroll__stage-item"]} data-sticky-scroll-stage-step={i + 1} data-active={activeStep === i + 1 ? "true" : undefined}>{step_0.stage}</div> : null)}</div></div>;
    $[27] = activeStep;
    $[28] = staged;
    $[29] = steps;
    $[30] = t26;
  } else {
    t26 = $[30];
  }
  let t27;
  if ($[31] !== activeStep || $[32] !== handleStepClick || $[33] !== showNav || $[34] !== steps) {
    t27 = showNav && <nav aria-label="Steps"><ul className={styles["helios-sticky-scroll__steps"]} data-split-overlay-target={true}>{steps.map((step_1, i_0) => <li key={i_0} className={styles["helios-sticky-scroll__step-item"]}><button type="button" className={classNames(styles["helios-sticky-scroll__step"], {
            [styles["helios-sticky-scroll__step--active"]]: activeStep === i_0 + 1
          })} onClick={() => handleStepClick(i_0 + 1)} aria-current={activeStep === i_0 + 1 ? "step" : undefined} data-split-overlay-target={true}><span className={styles["helios-sticky-scroll__step-label"]}>{step_1.label}</span><span className={classNames(styles["helios-sticky-scroll__step-label"], styles["helios-sticky-scroll__step-label--dark-overlay"])} aria-hidden="true">{step_1.label}</span></button></li>)}</ul></nav>;
    $[31] = activeStep;
    $[32] = handleStepClick;
    $[33] = showNav;
    $[34] = steps;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== sidebarFooter) {
    t28 = sidebarFooter && <div className={styles["helios-sticky-scroll__sidebar-footer"]}>{sidebarFooter}</div>;
    $[36] = sidebarFooter;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] !== t24 || $[39] !== t25 || $[40] !== t26 || $[41] !== t27 || $[42] !== t28) {
    t29 = <div className={styles["helios-sticky-scroll__sidebar"]}>{t24}{t25}{t26}{t27}{t28}</div>;
    $[38] = t24;
    $[39] = t25;
    $[40] = t26;
    $[41] = t27;
    $[42] = t28;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== staged || $[45] !== steps) {
    let t31;
    if ($[47] !== staged) {
      t31 = (step_2, i_1) => {
        const isDark = step_2.tone === "dark" || step_2.dark;
        return <div key={i_1} data-sticky-scroll-dark-section={isDark ? "true" : undefined} className={classNames(styles["helios-sticky-scroll__block"], {
          [styles["helios-sticky-scroll__block--dark"]]: isDark
        })}>{staged ? <div id={step_2.id} data-scroll-step={i_1 + 1} className={styles["helios-sticky-scroll__staged-step"]}>{hasStage(step_2) && <div className={styles["helios-sticky-scroll__inline-stage"]} data-sticky-scroll-inline-stage="" aria-hidden="true" inert={true}>{step_2.stage}</div>}<div className={styles["helios-sticky-scroll__staged-copy"]}>{step_2.content}</div></div> : <div id={step_2.id} data-scroll-step={i_1 + 1}>{step_2.content}</div>}</div>;
      };
      $[47] = staged;
      $[48] = t31;
    } else {
      t31 = $[48];
    }
    t30 = steps.map(t31);
    $[44] = staged;
    $[45] = steps;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[49] !== t30) {
    t31 = <div className={styles["helios-sticky-scroll__content"]}>{t30}</div>;
    $[49] = t30;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== t29 || $[52] !== t31) {
    t32 = <div className={styles["helios-sticky-scroll__layout"]}>{t29}{t31}</div>;
    $[51] = t29;
    $[52] = t31;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== t22 || $[55] !== t23 || $[56] !== t32) {
    t33 = <div className={t22}>{t23}{t32}</div>;
    $[54] = t22;
    $[55] = t23;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== instanceId || $[59] !== setContainerRef || $[60] !== t17 || $[61] !== t19 || $[62] !== t20 || $[63] !== t33) {
    t34 = <div ref={setContainerRef} data-helios-band={t17} data-helios-section={t18} data-sticky-scroll-id={instanceId} data-sticky-scroll-staged={t19} className={t20}>{t33}</div>;
    $[58] = instanceId;
    $[59] = setContainerRef;
    $[60] = t17;
    $[61] = t19;
    $[62] = t20;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  return t34;
}
function _temp(step) {
  return step.tone === "dark" || step.dark;
}
export default StickyScrollLayout;